import { useEffect, useState } from "react";
import { XMarkIcon, LockClosedIcon, PlusIcon } from "@heroicons/react/24/outline";
import FacilityAvatar from "../shared/FacilityAvatar";
import StatusBadge from "../shared/StatusBadge";
import FacilityDetailsTab from "./FacilityDetailsTab";
import FacilityThemeTab from "./FacilityThemeTab";

// Add/Edit facility slide-over. Add mode only shows the Details tab -- the
// Theme tab needs a facility_number, which only exists once the facility has
// been created (matches the "Theme can be set once the facility is created"
// note in the design).
const FacilityDrawer = ({ facility, onClose, onSaved, initialTab = "details" }) => {
  const isNew = !facility;
  const [activeTab, setActiveTab] = useState(isNew ? "details" : initialTab);

  useEffect(() => {
    setActiveTab(isNew ? "details" : initialTab);
  }, [facility?.id]);

  const handleDetailsSaved = (savedFacility) => {
    onSaved(savedFacility);
    if (isNew) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      <div className="relative flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-8 py-6">
          <div className="flex items-center gap-4">
            {isNew ? (
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EEF0FB] text-[#2B3674]">
                <PlusIcon className="h-5 w-5" />
              </div>
            ) : (
              <FacilityAvatar name={facility.name} logoUrl={facility.logo_urls?.desktop_app_logo} size="h-11 w-11" />
            )}
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">{isNew ? "Add facility" : facility.name}</h2>
                {!isNew && <StatusBadge isActive={facility.is_active} />}
              </div>
              <p className="text-sm text-gray-500">
                {isNew
                  ? "Fill in the details, then set the theme after creating"
                  : `Facility #${facility.facility_number}`}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-2 border-b border-gray-100 px-8 pt-4">
          <button
            type="button"
            onClick={() => setActiveTab("details")}
            className={`rounded-t-lg px-4 py-2 text-sm font-semibold ${
              activeTab === "details" ? "border-b-2 border-[#2B3674] text-[#2B3674]" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Details
          </button>
          <button
            type="button"
            onClick={() => !isNew && setActiveTab("theme")}
            disabled={isNew}
            className={`flex items-center gap-1.5 rounded-t-lg px-4 py-2 text-sm font-semibold ${
              isNew
                ? "cursor-not-allowed text-gray-300"
                : activeTab === "theme"
                ? "border-b-2 border-[#2B3674] text-[#2B3674]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Theme
            {isNew && <LockClosedIcon className="h-3.5 w-3.5" />}
          </button>
        </div>

        <div className="flex-1 px-8 py-6">
          {activeTab === "details" ? (
            <FacilityDetailsTab facility={facility} isNew={isNew} onSaved={handleDetailsSaved} onCancel={onClose} />
          ) : (
            <FacilityThemeTab facilityNumber={facility.facility_number} onCancel={onClose} />
          )}
        </div>
      </div>
    </div>
  );
};

export default FacilityDrawer;
